"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Children } from 'react';
import { validateSurface } from '../../globals.js';
import styles from './text-link-caret.module.scss.js';
import LeftCaretBold from '../../icons/component-icons/left-caret-bold/index.js';
import RightCaretBold from '../../icons/component-icons/right-caret-bold/index.js';

/**
 * @component TextLinkCaret
 * @package Buttons
 * @document ../_docs/code/TextLinkCaret.editor.tsx
 * @document ../_docs/code/TextLinkCaret.snippet.tsx
 * @document ../_docs/code/TextLinkCaret.import.tsx
 */
const TextLinkCaret = (_a) => {
    var { disabled, dataLoc, href, surface: surfaceProp = 'lightPrimary', role, ariaLabel, children, tabIndex, onKeyDown, onClick, iconPosition = 'right', 'aria-label': arialabel, 'data-testid': testId = 'text-link-caret' } = _a, rest = __rest(_a, ["disabled", "dataLoc", "href", "surface", "role", "ariaLabel", "children", "tabIndex", "onKeyDown", "onClick", "iconPosition", 'aria-label', 'data-testid']);
    // Validate surface prop
    const surface = validateSurface(surfaceProp);
    const linkStyles = [
        styles.textLinkCaret,
        styles[iconPosition],
        styles[surface.toLowerCase()],
    ];
    const iconWrapperStyles = [styles.iconWrapper, styles[iconPosition]];
    if (disabled)
        linkStyles.push(styles.disabled);
    const _calculateRole = () => {
        switch (true) {
            case !!role:
                return role;
            case !!href:
                return 'link';
            case !!onClick:
                return 'button';
            default:
                return undefined;
        }
    };
    const _calculateAriaLabel = () => ariaLabel ||
        arialabel ||
        (typeof children === 'string' ? children : 'Text Link Caret');
    const _onKeyDown = (e) => {
        if (onKeyDown)
            onKeyDown(e);
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
            e.target.click();
        }
    };
    const _formatChildren = (childArray) => {
        let textWithoutCaret = '';
        childArray.forEach((item, index) => {
            if (index !== childArray.length - 1) {
                textWithoutCaret += `${item} `;
            }
        });
        return textWithoutCaret;
    };
    const checkForNode = (childElements) => {
        if (!childElements)
            return { childArray: [], textWithCaret: '' };
        if (typeof childElements === 'string') {
            const trimmed = childElements.trim();
            // If all children are "strings", put last word with caret
            const childArray = trimmed.split(' ');
            const textWithCaret = childArray[childArray.length - 1];
            return {
                childArray: _formatChildren(childArray),
                textWithCaret,
            };
        }
        // Normalize children into an array
        const childArray = Children.toArray(childElements);
        if (childArray.length === 0) {
            return { childArray: [], textWithCaret: '' };
        }
        if (childArray.length === 1) {
            // Handle a single child (string, react elment, etc. )
            return { childArray: [], textWithCaret: childArray[0] };
        }
        return {
            childArray: childArray.slice(0, -1), // all elements expect last one
            textWithCaret: childArray[childArray.length - 1], // the last element
        };
    };
    const childArray = children ? checkForNode(children).childArray : [];
    const textWithCaret = children ? checkForNode(children).textWithCaret : '';
    const _onClick = (e) => {
        // reassign the target due to the hit area propogation
        const newEvent = e;
        newEvent.target = e.currentTarget;
        if (onClick)
            return onClick(newEvent);
    };
    return (jsx("a", Object.assign({}, rest, { className: linkStyles.join(' '), "data-testid": testId, "data-loc": dataLoc, href: href || undefined, role: _calculateRole(), "aria-disabled": disabled, "aria-label": _calculateAriaLabel(), tabIndex: disabled ? -1 : tabIndex || 0, onKeyDown: _onKeyDown, onClick: _onClick, children: jsx("div", { className: styles.contentWrapper, tabIndex: -1, "aria-hidden": true, children: jsxs("div", { className: styles.inlineWrapper, 
                // eslint-disable-next-line
                role: "text", children: [iconPosition === 'left' && (jsx("div", { className: iconWrapperStyles.join(' '), tabIndex: -1, "aria-hidden": true, children: jsx(LeftCaretBold, { ariaHidden: true, tabIndex: -1, "aria-label": "left-caret-bold icon", size: 12 }) })), jsxs("div", { className: styles.childWrapper, tabIndex: -1, "aria-hidden": true, children: [childArray, jsxs("div", { className: styles.textWrapper, children: [textWithCaret, iconPosition === 'right' && (jsx("div", { className: iconWrapperStyles.join(' '), tabIndex: -1, "aria-hidden": true, children: jsx(RightCaretBold, { ariaHidden: true, tabIndex: -1, "aria-label": "right-caret-bold icon", size: 12 }) }))] })] })] }) }) })));
};

export { TextLinkCaret, TextLinkCaret as default };
